import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Tab, Tabs, Tooltip } from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  // setStatus,
} from '../../store/users/usersSlice';
import {
  findUser,
  updateUserProfile,
  createUsers,
  createCustomer,
} from '../../store/users/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import {
  reset as resetUsers,
  setStatus as setUsersStatus,
} from '../../store/users/usersSlice';
import userSchema from '../../validation/userSchema';
import UsersFormModal from './UsersFormModal';
import { Users } from '../../store/users/api/interfaces';
import { Link, useNavigate } from 'react-router-dom';
import { encryptString } from '../../utils/url';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
// Direct import of Users service
import { Users as UsersService } from '../../client/services';
import {
  getInferredNationalityDisplay,
  getInferredNationalityLabel,
  shouldShowNationalityTooltip,
} from '../../utils/nationalityInference';

const renderUserNationality = (user: Users) => {
  const displayValue = getInferredNationalityDisplay(user);

  if (!shouldShowNationalityTooltip(user)) {
    return displayValue;
  }

  return (
    <Tooltip title={getInferredNationalityLabel(user)} arrow>
      <Box component='span' sx={{ cursor: 'help' }}>
        {displayValue}
      </Box>
    </Tooltip>
  );
};

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.user);
  const [search, setSearch] = useState<string>('');
  const { toggle, onOpen, onClose } = useToggle();

  // Initialize tab based on URL if present
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    return tabParam === '1' ? 1 : 0; // Default to 0 if not explicitly set to 1
  };

  const [tabValue, setTabValue] = useState<number>(getInitialTab());
  const [isAdminEditing, setIsAdminEditing] = useState<boolean>(false);

  // Add a flag to track if we need to load data from useEffect
  const [skipEffectLoad, setSkipEffectLoad] = useState(false);
  
  // Update tab based on URL and store other API params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    // If tab param exists, use it and then remove it from URL
    if (params.has('tab')) {
      const tabParam = params.get('tab');
      if (tabParam === '1' && tabValue !== 1) {
        setTabValue(1);
      } else if (tabParam === '0' && tabValue !== 0) {
        setTabValue(0);
      }
      
      // Remove only the tab parameter but keep all API parameters
      params.delete('tab');
      const newUrl = params.toString() 
        ? `${window.location.pathname}?${params}` 
        : window.location.pathname;
      
      // Replace current URL without tab parameter (without adding to browser history)
      window.history.replaceState({}, '', newUrl);
    }
  }, [tabValue]);

  // Function to forcefully refresh any tab's data (customers or staff)
  const refreshData = async (isCustomerTab = false) => {
    try {
      console.log(`Refreshing ${isCustomerTab ? 'Customers' : 'Staff'} data`);
      
      // Set loading state
      dispatch(setUsersStatus({ status: 'pending' }));
      
      // Make direct API call
      const response = await UsersService.find({
        query: {
          limit,
          skip,
          isUser: isCustomerTab,
          ...(search && { search })
        }
      });
      
      // Update the store with new data
      if (response && response.data) {
        // Force update the store
        dispatch(findUser.fulfilled(response, '', {
          limit,
          skip,
          isUser: isCustomerTab
        }));
        console.log(`Data refreshed: ${response.data.length} records`);
      }
      
      return true;
    } catch (error) {
      console.error('Error refreshing data:', error);
      dispatch(setUsersStatus({ status: 'failed' }));
      return false;
    }
  };
  
  // Function to forcefully refresh staff data - maintains backward compatibility
  const refreshStaffData = async () => {
    // Set tab to staff if not already there
    if (tabValue !== 1) {
      setTabValue(1);
    }
    
    // Use the generic refresh function
    return refreshData(false);
  };

  // Handle regular data loading through useEffect
  useEffect(() => {
    // Skip loading if we just did a direct refresh
    if (skipEffectLoad) {
      setSkipEffectLoad(false);
      return;
    }
    
    // Get parameters from URL if present
    const params = new URLSearchParams(window.location.search);
    const urlLimit = params.get('limit') ? parseInt(params.get('limit') || '0') : limit;
    const urlSkip = params.get('skip') ? parseInt(params.get('skip') || '0') : skip;
    const urlIsUser = params.get('isUser') === 'false' ? false : 
                      params.get('isUser') === 'true' ? true : 
                      tabValue === 0; // Default based on tab if not specified
    const urlSearch = params.get('search') || search;

    // Use params from URL or defaults
    dispatch(
      findUser({
        limit: urlLimit,
        skip: urlSkip,
        ...(urlSearch && { search: urlSearch }),
        isUser: urlIsUser
      })
    );
  }, [dispatch, limit, skip, tabValue, search, skipEffectLoad]);

  // Enhance tab change handler to force refresh
  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    // Update tab state
    setTabValue(newValue);
    
    // Clear any search terms
    setSearch('');
    
    // Reset pagination when switching tabs
    if (currentPage !== 0) {
      dispatch(setCurrentPage({ currentPage: 0 }));
      dispatch(setSkip({ skip: 0 }));
    }
    
    // Set flag to skip the useEffect data load since we'll do it directly
    setSkipEffectLoad(true);
    
    // Use our generic refresh function
    await refreshData(newValue === 0);
  };

  useEffect(() => {
    dispatch(resetUsers());
  }, [search]);

  const {
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik({
    initialValues: {
      id: '',
      firstName: '',
      lastName: '',
      countryCallingCode: '',
      mobileNumber: '',
      email: '',
      userRole: '',
      allowNotification: false,
      // language: '',
      // countryId: '',
      // regionId: '',
      activeState: true,
      gender: '',
    },
    validationSchema: userSchema,
    onSubmit: async (values) => {
      try {
        console.log("handleSubmit called in index.tsx", values);
        
        // Track if operation was successful - assume success unless an error is thrown
        let isSuccess = true;
        
        try {
          // Security check - ensure existing users can't be changed to admin
          // But allow creating new admin users
          if (values.userRole === 'admin' && values.id && values.id.length > 0) {
            // Only allow if it was already an admin
            const existingUser = data.find(user => user.id === values.id);
            if (existingUser && existingUser.userRole !== 'admin') {
              console.error("Attempted to change non-admin user to admin");
              return false;
            }
          }
          
          if (values.id) {
            // Create a copy of the original values
            const originalData = { ...values };
            
            // If editing an admin user, just send the allowed fields
            if (values.userRole === 'admin') {
              const response = await dispatch(updateUserProfile({
                id: originalData.id,
                firstName: originalData.firstName,
                lastName: originalData.lastName,
                activeState: originalData.activeState,
                allowNotification: originalData.allowNotification,
                // Keeping these fields but preventing UI changes
                countryCallingCode: originalData.countryCallingCode,
                mobileNumber: originalData.mobileNumber,
                email: originalData.email,
                userRole: originalData.userRole
              })).unwrap();
              
              console.log("Admin update response:", response);
            } else {
              // For non-admin users, send everything
              const response = await dispatch(updateUserProfile(originalData)).unwrap();
              console.log("Regular user update response:", response);
            }
          } else if (!values.id) {
            // Create a new user
            // Extract only the fields we need for creating a user
            const createUserData = {
              firstName: values.firstName,
              lastName: values.lastName,
              email: values.email,
              mobileNumber: values.mobileNumber,
              countryCallingCode: values.countryCallingCode,
              userRole: values.userRole,
              allowNotification: values.userRole === 'admin' ? values.allowNotification : false
            };
            
            // Call API to create user
            const createResponse = await dispatch(createUsers(createUserData)).unwrap();
            console.log("Create user response:", createResponse);
          }
        } catch (apiError) {
          console.error("API call failed:", apiError);
          isSuccess = false;
        }
        
        console.log("Direct API call result:", isSuccess);
        
        // Don't do the refresh here - it will be done by the modal
        
        // Return a simple boolean - true for success, false for failure
        return isSuccess;
      } catch (error) {
        console.error("Error in form submission:", error);
        return false;
      }
    },
  });

  const handleUpdate = (value: Users): void => {
    // Set flag if editing an admin user
    if (value?.userRole === 'admin') {
      setIsAdminEditing(true);
    } else {
      setIsAdminEditing(false);
    }
    
    onOpen();
    setValues({
      id: value.id,
      firstName: value?.firstName,
      lastName: value?.lastName,
      countryCallingCode: value?.countryCallingCode,
      mobileNumber: value?.mobileNumber,
      email: value?.email,
      userRole: value?.userRole,
      allowNotification: value.allowNotification,
      activeState: value.activeState,
      // Gender may be missing in Users type; default to empty string
      gender: (value as any)?.gender ?? '',
    });
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setUsersStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
    // dispatch(
    //   findUser({
    //     limit,
    //     skip,
    //     ...(e.target.value && { search: e.target.value }),
    //   })
    // );
  };

  // Add this useEffect to monitor changes to the user data
  useEffect(() => {
    console.log("User data updated:", data);
  }, [data]);
  
  // Additional useEffect to force refresh on component mount
  useEffect(() => {
    // Refresh the data when component mounts
    const initData = async () => {
      if (tabValue === 1) {
        await refreshStaffData();
      }
    };
    
    initData();
  }, []);

  const [selectedUserToDelete, setSelectedUserToDelete] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  // Add a handler for delete button click
  const handleDeleteClick = (userId: string): void => {
    setSelectedUserToDelete(userId);
    setOpenDeleteDialog(true);
  };

  // Handle the actual deletion
  const handleDeleteConfirm = async (): Promise<void> => {
    if (!selectedUserToDelete) return;
    
    try {
      // Call the delete API endpoint
      await UsersService.remove(selectedUserToDelete);
      
      // Close the dialog
      setOpenDeleteDialog(false);
      setSelectedUserToDelete(null);
      
      // Reset pagination when switching tabs
      if (currentPage !== 0) {
        dispatch(setCurrentPage({ currentPage: 0 }));
        dispatch(setSkip({ skip: 0 }));
      }
      
      // Set flag to skip the useEffect data load since we'll do it directly
      setSkipEffectLoad(true);
      
      // Force a direct data refresh - exactly like handleTabChange
      try {
        // Force immediate data refresh with a direct API call
        const response = await UsersService.find({
          query: {
            limit,
            skip: 0,  // Reset to first page
            isUser: tabValue === 0,  // true for Customers, false for Staff
          }
        });
        
        if (response && response.data) {
          // Update Redux store directly with fresh data
          dispatch(findUser.fulfilled(response, '', {
            limit,
            skip: 0,
            isUser: tabValue === 0
          }));
          console.log(`Data refreshed after delete:`, response.data.length);
        }
      } catch (error) {
        console.error("Error refreshing data:", error);
        // Even if refresh fails, still update the UI state
        dispatch(setUsersStatus({ status: 'failed' }));
      }
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleDeleteCancel = (): void => {
    setOpenDeleteDialog(false);
    setSelectedUserToDelete(null);
  };

  // Function to refresh the current tab's data
  const refreshCurrentTab = async (): Promise<void> => {
    console.log(`Refreshing current tab (${tabValue === 0 ? 'Customers' : 'Staff'})`);
    
    // Set loading state
    dispatch(setUsersStatus({ status: 'pending' }));
    
    try {
      // Force immediate data refresh with a direct API call
      const response = await UsersService.find({
        query: {
          limit,
          skip: 0,  // Reset to first page
          isUser: tabValue === 0,  // true for Customers, false for Staff
          ...(search && { search }) // Keep any active search
        }
      });
      
      if (response && response.data) {
        // Update Redux store directly with fresh data
        dispatch(findUser.fulfilled(response, '', {
          limit,
          skip: 0,
          isUser: tabValue === 0
        }));
        console.log(`Tab refresh completed: ${response.data.length} records`);
        return;
      }
    } catch (error) {
      console.error("Error refreshing current tab:", error);
      dispatch(setUsersStatus({ status: 'failed' }));
      throw error; // Re-throw to indicate failure
    }
  };

  const handleNewClick = () => {
    // If in Customers tab, preset role to 'user' and clear id
    if (tabValue === 0) {
      setValues({
        id: '',
        firstName: '',
        lastName: '',
        countryCallingCode: '',
        mobileNumber: '',
        email: '',
        userRole: 'user',
        allowNotification: false,
        activeState: true,
        gender: '',
      });
    } else {
      setValues({
        id: '',
        firstName: '',
        lastName: '',
        countryCallingCode: '',
        mobileNumber: '',
        email: '',
        userRole: '',
        allowNotification: false,
        activeState: true,
        gender: '',
      });
    }
    onOpen();
  };

  const tableColSpan = 7;

  return (
    <>
      <Page title={'Users: List'}>
        <Container maxWidth={'xl'}>
          <HeaderBreadcrumbs
            heading={'Users'}
            links={[{ name: 'Dashboard', href: '/' }]}
            action={
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={handleNewClick}
              >
                {tabValue === 0 ? 'New Customer' : 'New User'}
              </Button>
            }
          />

          <Box
            sx={{
              maxWidth: { xs: 550, sm: 850, md: 1150, lg: 1500 },
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant='scrollable'
              scrollButtons={true}
              aria-label='scrollable auto tabs example'
            >
              <Tab label={'Customers'} />
              <Tab label={'Staff'} />
            </Tabs>
          </Box>

          <Card>
            <Searchbar handleSearch={handleSearch} />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Name'}</TableCell>
                      <TableCell align='center'>{'Email'}</TableCell>
                      <TableCell align='center'>{'Mobile Number'}</TableCell>
                      <TableCell align='center'>{'Role'}</TableCell>
                      {tabValue === 1 && (
                        <TableCell align='center'>{'Nationality'}</TableCell>
                      )}
                      {tabValue === 0 && (
                        <TableCell align='center'>{'Balance'}</TableCell>
                      )}
                      <TableCell align='center'>{'Status'}</TableCell>
                      {/* {tabValue === 0 && ( */}
                      <TableCell align='center'>{'Action'}</TableCell>
                      {/* )} */}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={tableColSpan}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={tableColSpan}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell align='center'>
                            {user?.firstName} {user?.lastName}
                          </TableCell>
                          <TableCell align='center'>{user?.email}</TableCell>
                          <TableCell align='center'>
                            {user?.countryCallingCode}-{user?.mobileNumber}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{ textTransform: 'capitalize' }}
                          >
                            {user?.userRole}
                          </TableCell>
                          {tabValue === 1 && (
                            <TableCell align='center'>
                              {renderUserNationality(user)}
                            </TableCell>
                          )}
                          {tabValue === 0 && (
                            <TableCell align='center'>
                              {user?.balance}
                            </TableCell>
                          )}
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={user.activeState ? 'success' : 'error'}
                            >
                              {user.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          {tabValue === 1 && (
                            <TableCell align='center'>
                              <IconButton
                                aria-label='edit'
                                onClick={() => handleUpdate(user)}
                              >
                                <Iconify icon={'eva:edit-fill'} />
                              </IconButton>
                              {/* Only show delete button for non-admin users */}
                              {user.userRole !== 'admin' && (
                                <IconButton
                                  aria-label='delete'
                                  onClick={() => handleDeleteClick(user.id)}
                                  sx={{ color: 'error.main' }}
                                >
                                  <Iconify icon={'eva:trash-2-outline'} />
                                </IconButton>
                              )}
                            </TableCell>
                          )}
                          {tabValue === 0 && (
                            <TableCell align='center'>
                              <Stack
                                direction='row'
                                alignItems='center'
                                justifyContent='center'
                                spacing={0.5}
                              >
                                <IconButton
                                  size='small'
                                  aria-label='visibility'
                                  sx={{ color: '#808080' }}
                                >
                                  <Link
                                    to={`/users/view/${encryptString(
                                      user?.id ?? ''
                                    )}`}
                                    style={{
                                      color: 'inherit',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <RemoveRedEyeIcon sx={{ fontSize: 20 }} />
                                  </Link>
                                </IconButton>
                                <IconButton
                                  size='small'
                                  aria-label='edit'
                                  onClick={() =>
                                    navigate(
                                      `/users/edit/${encryptString(user?.id ?? '')}`
                                    )
                                  }
                                  sx={{ color: '#808080' }}
                                >
                                  <Iconify
                                    icon={'eva:edit-fill'}
                                    sx={{ width: 20, height: 20 }}
                                  />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <UsersFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={async (values) => {
              try {
                console.log("handleSubmit called in index.tsx", values);
                
                // Track if operation was successful - assume success unless an error is thrown
                let isSuccess = true;
                
                try {
                  // Security check - ensure existing users can't be changed to admin
                  // But allow creating new admin users
                  if (values.userRole === 'admin' && values.id && values.id.length > 0) {
                    // Only allow if it was already an admin
                    const existingUser = data.find(user => user.id === values.id);
                    if (existingUser && existingUser.userRole !== 'admin') {
                      console.error("Attempted to change non-admin user to admin");
                      return false;
                    }
                  }
                  
                  if (values.id) {
                    // Create a copy of the original values
                    const originalData = { ...values };
                    
                    // If editing an admin user, just send the allowed fields
                    if (values.userRole === 'admin') {
                      const response = await dispatch(updateUserProfile({
                        id: originalData.id,
                        firstName: originalData.firstName,
                        lastName: originalData.lastName,
                        activeState: originalData.activeState,
                        allowNotification: originalData.allowNotification,
                        // Keeping these fields but preventing UI changes
                        countryCallingCode: originalData.countryCallingCode,
                        mobileNumber: originalData.mobileNumber,
                        email: originalData.email,
                        userRole: originalData.userRole
                      })).unwrap();
                      
                      console.log("Admin update response:", response);
                    } else {
                      // For non-admin users, send everything
                      const response = await dispatch(updateUserProfile(originalData)).unwrap();
                      console.log("Regular user update response:", response);
                    }
                  } else if (!values.id) {
                    if (tabValue === 0) {
                      // Create a new customer
                      const payload = {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        mobileNumber: values.mobileNumber,
                        countryCallingCode: values.countryCallingCode,
                        countryCode: values.countryCode || 'QA', // Default to Qatar if not provided
                        gender: values.gender,
                      };
                      const resp = await dispatch(createCustomer(payload)).unwrap();
                      console.log('Create customer response:', resp);
                    } else {
                      // Create a new staff user
                      const createUserData = {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        mobileNumber: values.mobileNumber,
                        countryCallingCode: values.countryCallingCode,
                        userRole: values.userRole,
                        allowNotification: values.userRole === 'admin' ? values.allowNotification : false
                      };
                      const createResponse = await dispatch(createUsers(createUserData)).unwrap();
                      console.log("Create user response:", createResponse);
                    }
                  }
                } catch (apiError) {
                  console.error("API call failed:", apiError);
                  isSuccess = false;
                }
                
                console.log("Direct API call result:", isSuccess);
                
                // Don't do the refresh here - it will be done by the modal
                
                // Return a simple boolean - true for success, false for failure
                return isSuccess;
              } catch (error) {
                console.error("Error in form submission:", error);
                return false;
              }
            }}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
            isAdminEditing={isAdminEditing}
            refreshTable={refreshCurrentTab}
            isCustomerCreation={tabValue === 0}
          />
          
          {/* Confirmation Dialog for Delete */}
          <Dialog
            open={openDeleteDialog}
            onClose={handleDeleteCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {"Confirm User Deletion"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to delete this user? This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDeleteCancel} color="primary">
                Cancel
              </Button>
              <Button onClick={handleDeleteConfirm} color="error" autoFocus>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Page>
    </>
  );
};

export default Index;
